import { useMutation, useQueryClient } from '@tanstack/react-query';
import useHttpClient from './useHttpClient';

export enum PersonType {
  Adjuster = 'Adjuster',
  Attorney = 'Attorney',
  Client = 'Client',
  Court = 'Court',
  Defendant = 'Defendant',
  Plaintiff = 'Plaintiff',
  Expert = 'Expert',
  Firm = 'Firm',
  InsuranceCompany = 'Insurance Company',
  InvolvedParty = 'Involved Party',
  Judge = 'Judge',
  MedicalProvider = 'Medical Provider',
}

export enum CreateContactAction {
  Add = 'ADD',
  Update = 'UPDATE',
}

export enum CreateContactSelector {
  FirstName = 'firstName',
  LastName = 'lastName',
  Emails = 'emails',
  Phones = 'phones',
  PersonTypes = 'personTypes',
}

interface IEmailAddress {
  address: string;
}

interface IPhoneNumber {
  number: string;
}

export interface IContactDataReq {
  selector: CreateContactSelector;
  action: CreateContactAction;
  value: string | number | boolean | IEmailAddress | IPhoneNumber | PersonType;
}

export interface IContactDataRes {
  firstName: string;
  lastName?: string;
  fullName?: string;
  primaryEmail?: string;
  emails?: {
    address: string;
  }[];
  phones?: {
    number: string;
  }[];
  personId: {
    native: number;
  };
}

export interface IOrgContactCreateStateObj {
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  phoneNumber?: string;
  personType?: number;
}

export interface IContactPayload {
  addContact: (payload: IOrgContactCreateStateObj) => Promise<IContactDataRes>;
  loading: boolean;
  error?: Error;
  called: boolean;
  contact?: IContactDataRes;
}

export const updateContactRequestPayload = (contact: IOrgContactCreateStateObj) => {
  const contactRequestPayload: IContactDataReq[] = [];
  if (contact?.firstName) {
    contactRequestPayload.push({
      selector: CreateContactSelector.FirstName,
      action: CreateContactAction.Update,
      value: contact.firstName,
    });
  }
  if (contact?.lastName) {
    contactRequestPayload.push({
      selector: CreateContactSelector.LastName,
      action: CreateContactAction.Update,
      value: contact.lastName,
    });
  }
  if (contact?.emailAddress) {
    contactRequestPayload.push({
      selector: CreateContactSelector.Emails,
      action: CreateContactAction.Add,
      value: { address: contact.emailAddress },
    });
  }
  if (contact?.phoneNumber) {
    contactRequestPayload.push({
      selector: CreateContactSelector.Phones,
      action: CreateContactAction.Add,
      value: { number: contact.phoneNumber },
    });
  }
  if (contact?.personType) {
    contactRequestPayload.push({
      selector: CreateContactSelector.PersonTypes,
      action: CreateContactAction.Add,
      value: contact.personType,
    });
  }
  return contactRequestPayload;
};

const useCreateOrgContact = (): IContactPayload => {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (contactData: IOrgContactCreateStateObj) => {
      const payload = updateContactRequestPayload(contactData);
      const response = await httpClient.post('/custom-contacts', payload);
      return response.data as IContactDataRes;
    },
    onSuccess: () => {
      // Invalidate org contacts queries to refresh the list
      queryClient.invalidateQueries(['orgContacts']);
    },
  });

  const addContact = async (contactData: IOrgContactCreateStateObj) => {
    return mutation.mutateAsync(contactData);
  };

  return {
    addContact,
    error: mutation.error as Error,
    loading: mutation.isPending,
    called: mutation.isSuccess || mutation.isError,
    contact: mutation.data,
  };
};

export default useCreateOrgContact;
